import { ArrowBackOutlined, ArticleOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ETeamSize } from "../../../shared/enums/client/EClient";
import { handleError } from "../../../shared/functions/handleError";
import { IClient } from "../../../shared/interfaces/client/IClient";
import { lightPalette } from "../../../shared/mui/palettes";
import { downloadFile } from "../../../utils/api/api.service";
import { OFFER_URL } from "../../../utils/api/endpoints";
import { RootState } from "../../../store/store";
import { ClientService } from "../service";

const getMultiplier = (teamSize: ETeamSize): number => {
  const map: Record<ETeamSize, number> = {
    [ETeamSize.UpTo20]: 1.0,
    [ETeamSize.From20To60]: 1.5,
    [ETeamSize.From60To100]: 2.0,
    [ETeamSize.Over100]: 3.0,
  };
  return map[teamSize] ?? 1.0;
};

const OfferPreview = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.companySettings);

  const [client, setClient] = useState<IClient | null>(null);
  const [lang, setLang] = useState<"rs" | "gb">("rs");
  const [signingDate, setSigningDate] = useState<moment.Moment | null>(null);
  const [signingPlace, setSigningPlace] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [hostingAtClient, setHostingAtClient] = useState(true);
  const [moduleOverrides, setModuleOverrides] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    if (id) {
      ClientService.getSingle(Number(id)).then(setClient).catch(handleError);
    }
  }, [id]);

  const effectiveModulePrice = (moduleId: number, basePrice: number) =>
    moduleOverrides[moduleId] !== undefined
      ? Number(moduleOverrides[moduleId])
      : basePrice;

  const modulesSubtotal =
    client?.modules?.reduce(
      (sum, m) => sum + effectiveModulePrice(m.id, Number(m.price)),
      0,
    ) ?? 0;
  const multiplier = client ? getMultiplier(client.teamSize) : 1;
  const calculatedTotal = modulesSubtotal * multiplier;
  const teamSizeSurcharge = calculatedTotal - modulesSubtotal;
  const showSurcharge = multiplier > 1;
  const hasModuleOverrides = Object.keys(moduleOverrides).length > 0;

  const handleModuleOverride = (moduleId: number, value: string) => {
    setModuleOverrides((prev) => ({ ...prev, [moduleId]: value }));
  };

  const buildBody = () => {
    const body: Record<string, unknown> = { lang, hostingAtClient };
    if (signingDate) body.signingDate = signingDate.format("DD.MM.YYYY");
    if (signingPlace) body.signingPlace = signingPlace;
    if (hasModuleOverrides) {
      body.moduleOverrides = Object.entries(moduleOverrides).map(
        ([id, price]) => ({ id: Number(id), price: Number(price) }),
      );
    }
    return body;
  };

  const handleDownload = async () => {
    if (!client?.id) return;
    setIsDownloading(true);
    await downloadFile(`${OFFER_URL}/${client.id}/download`, "pdf", `Offer - ${client.name}`, buildBody())
      .catch(handleError)
      .finally(() => setIsDownloading(false));
  };

  const handleDownloadDocx = async () => {
    if (!client?.id) return;
    setIsDownloadingDocx(true);
    await downloadFile(`${OFFER_URL}/${client.id}/download-docx`, "docx", `Offer - ${client.name}`, buildBody())
      .catch(handleError)
      .finally(() => setIsDownloadingDocx(false));
  };

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("offer.offerPreview")}
            {client?.name && ` — ${client.name}`}
          </Typography>

          {/* Controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <FormControl>
              <RadioGroup
                row
                value={lang}
                onChange={(e) => setLang(e.target.value as "rs" | "gb")}
              >
                <FormControlLabel
                  value="rs"
                  control={<Radio size="small" />}
                  label={t("language.rs")}
                />
                <FormControlLabel
                  value="gb"
                  control={<Radio size="small" />}
                  label={t("language.gb")}
                />
              </RadioGroup>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label={t("general.signingDate")}
                value={signingDate}
                onChange={(val) => setSigningDate(val)}
                format="DD.MM.YYYY"
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
            </LocalizationProvider>
            <TextField
              label={t("general.signingPlace")}
              value={signingPlace}
              onChange={(e) => setSigningPlace(e.target.value)}
              size="small"
              sx={{ width: 160 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hostingAtClient}
                  onChange={(e) => setHostingAtClient(e.target.checked)}
                  size="small"
                />
              }
              label={t("offer.hostingAtClient")}
            />
          </Box>
        </Box>
      </Card>

      {client && (
        <Card>
          {/* Parties */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle2"
                color={lightPalette.text.gray}
                sx={{ mb: 1 }}
              >
                {t("offer.licensor")}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {company.title}
              </Typography>
              {company.address && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  {company.address}{company.city ? `, ${company.city}` : ""}
                </Typography>
              )}
              {company.registrationNumber && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  Registarski broj: {company.registrationNumber}
                </Typography>
              )}
              {company.jib && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  JIB: {company.jib}
                </Typography>
              )}
              {company.pdvBroj && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  PDV broj: {company.pdvBroj}
                </Typography>
              )}
              {company.representativeName && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  {t("general.contactPerson")}: {company.representativeName}
                </Typography>
              )}
              {company.bankAccount && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  IBAN: {company.bankAccount}{company.swift ? ` | SWIFT: ${company.swift}` : ""}{company.bankName ? ` — ${company.bankName}` : ""}
                </Typography>
              )}
              {company.email && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  {company.email}
                </Typography>
              )}
              {company.phoneNumber && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  {company.phoneNumber}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="subtitle2"
                color={lightPalette.text.gray}
                sx={{ mb: 1 }}
              >
                {t("offer.licensee")}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {client.name}
              </Typography>
              <Typography variant="body2" color={lightPalette.text.gray}>
                {client.address}
              </Typography>
              {client.mb && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  {t("general.mb")}: {client.mb}
                </Typography>
              )}
              {client.pib && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  {t("general.pib")}: {client.pib}
                </Typography>
              )}
              {client.contactPerson && (
                <Typography variant="body2" color={lightPalette.text.gray}>
                  {t("general.contactPerson")}: {client.contactPerson}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          {/* Modules table */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t("general.modules")}
            <Chip
              label={t(`client.teamSize.${client.teamSize}`)}
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: lightPalette.background.paper }}>
                <TableCell>
                  <strong>{t("offer.module")}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{t("offer.annualFee")} (EUR)</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {client.modules?.map((module) => (
                <TableRow key={module.id}>
                  <TableCell>
                    {t(`modules.${module.name}`, { defaultValue: module.name })}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 160 }}>
                    <TextField
                      value={
                        moduleOverrides[module.id] !== undefined
                          ? moduleOverrides[module.id]
                          : Number(module.price).toFixed(2)
                      }
                      onChange={(e) =>
                        handleModuleOverride(module.id, e.target.value)
                      }
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: "0.01" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">EUR</InputAdornment>
                        ),
                      }}
                      sx={{ width: 140 }}
                    />
                  </TableCell>
                </TableRow>
              ))}

              {/* Subtotal */}
              <TableRow>
                <TableCell
                  sx={{ borderTop: `2px solid ${lightPalette.primary.main}` }}
                >
                  <strong>{t("offer.subtotal")}</strong>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ borderTop: `2px solid ${lightPalette.primary.main}` }}
                >
                  <strong>{modulesSubtotal.toFixed(2)}</strong>
                </TableCell>
              </TableRow>

              {/* Surcharge */}
              {showSurcharge && (
                <TableRow>
                  <TableCell sx={{ fontStyle: "italic" }}>
                    {t("offer.teamSizeSurcharge")} (
                    {t(`client.teamSize.${client.teamSize}`)}) &nbsp;×
                    {multiplier}
                  </TableCell>
                  <TableCell align="right" sx={{ fontStyle: "italic" }}>
                    {teamSizeSurcharge.toFixed(2)}
                  </TableCell>
                </TableRow>
              )}

              {/* Total */}
              <TableRow sx={{ backgroundColor: lightPalette.background.paper }}>
                <TableCell>
                  <Typography fontWeight="bold">
                    {t("offer.totalAnnual")}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    fontWeight="bold"
                    color={lightPalette.primary.main}
                  >
                    {calculatedTotal.toFixed(2)} EUR
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackOutlined />}
            >
              {t("button.cancel")}
            </Button>
            <LoadingButton
              loading={isDownloadingDocx}
              variant="outlined"
              onClick={handleDownloadDocx}
              disabled={!client}
              startIcon={<ArticleOutlined />}
            >
              {t("button.download")} DOCX
            </LoadingButton>
            <LoadingButton
              loading={isDownloading}
              variant="contained"
              onClick={handleDownload}
              disabled={!client}
              startIcon={<FileDownloadOutlined />}
            >
              {t("button.download")} PDF
            </LoadingButton>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default OfferPreview;
